import { useEffect, useMemo, useState } from 'react'
import { isEmpty, isEqual, max, omitBy, pick, size, slice } from 'lodash'

/**
 * @typedef {import('../../definitions').SelectOption} SelectOption
 */

/**
 * @typedef {React.MouseEvent<HTMLButtonElement>} ButtonEvent
 */

/**
 * @typedef {(event: ButtonEvent, pageIndex: number) => void} HandlePageChange
 * @typedef {(pageIndex: string) => void} HandleJumpToPage
 * @typedef {(pageItemsNumber: string) => void} HandleRowsPerPage
 */

/**
 * @typedef {Object} SearchHandlers
 * @property {HandlePageChange} handleChangePage
 * @property {HandleJumpToPage} handleJumpToPage
 * @property {HandleRowsPerPage} handleRowsPerPage
 * @property {(state: boolean) => void} setLoading
 * @property {(state: boolean) => void} setHasNext
 * @property {(results: any) => void} setSearchResults
 * @property {(filterParams: any) => void} startNewSearch
 */

/**
 * @typedef {Object} SearchState
 * @property {any} currentFilters
 * @property {number} currentPage
 * @property {string} currentPageIndex
 * @property {boolean} loading
 * @property {boolean} hasNext
 * @property {any} pageResults
 * @property {SelectOption[]} pagesIndexOptions
 * @property {number} resultsCount
 * @property {number} rowsPerPage
 */

/**
 * @typedef {Object} SearchStateHook
 * @property {SearchHandlers} handlers
 * @property {SearchState} state
 */

/**
 * @typedef {Object} SearchStateProps
 * @property {any} defaultFilters
 * @property {any} defaultSearchResults
 * @property {string[]} supportedFilters
 * @property {function} searchRequestPromise
 */

/**
 * Course search handlers and local state business logic.
 * @param {SearchStateProps} props
 * @return {SearchStateHook}
 */
const useSearchState = ({
  defaultFilters,
  supportedFilters,
  searchRequestPromise,
  defaultSearchResults,
}) => {
  const [loading, setLoading] = useState(false)
  const [searchResults, setSearchResults] = useState(defaultSearchResults)
  const [hasNext, setHasNext] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const defaultRowsPerPage = 10
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)
  const [currentFilters, setCurrentFilters] = useState(defaultFilters)

  /**
   * @type {number[]}
   */
  const defaultReceivedPageIndexes = []
  const [receivedPageIndexes, setReceivedPageIndexes] = useState(defaultReceivedPageIndexes)

  /**
   * Reset search local state.
   */
  const resetSearchState = () => {
    setCurrentPage(0)
  }

  /**
   * Start a new search.
   * @param {any} filterParams
   * @return {Promise<void>}
   */
  const startNewSearch = async (filterParams) => {
    const finalFilterParams = omitBy(pick(filterParams, supportedFilters), isEmpty)
    setLoading(true)
    await processSearch({ ...finalFilterParams, page: 1 }, true)
    if (!isEqual(currentFilters, finalFilterParams)) {
      setCurrentFilters(finalFilterParams)
    }

    setLoading(false)
  }

  /**
   * Process course sections search.
   * @param {any} filterParams
   * @param {boolean} resetResults
   */
  const processSearch = async (filterParams, resetResults = false) => {
    // Data for specific page is already in local state, skip data fetch.
   
    if (receivedPageIndexes.includes(filterParams.page) && !resetResults) {
      return
    }
    const result = await searchRequestPromise({ ...filterParams, pageSize: 200 })

    if (result) {
      if (resetResults) {
        resetSearchState()
        setSearchResults(result.pageItems)
        setReceivedPageIndexes([filterParams.page])
      } else {
        setReceivedPageIndexes(receivedPageIndexes.concat([filterParams.page]))
      }

      setHasNext(result.hasNextPage)
    }
  }

  /**
   * Handle page change.
   * @type {HandlePageChange}
   * @param {React.MouseEvent<HTMLButtonElement>} event
   * @param {number} pageIndex
   */
  const handleChangePage = async (event, pageIndex) => {
    setCurrentPage(pageIndex)
    setLoading(true)

    await processSearch({
      ...currentFilters,
      page: pageIndex + 1,
    })
    setLoading(false)
  }

  // Estimate results count considering next items when next page is available.
  const resultsCount = useMemo(() => {
    const nextResultsCount = !hasNext && 0
    return size(searchResults) + nextResultsCount
  }, [searchResults, rowsPerPage, hasNext])

  // Select current page results.
  const pageResults = useMemo(() => {
    const startResultIndex = currentPage * rowsPerPage
    const endResultIndex = startResultIndex + rowsPerPage
    return slice(searchResults, startResultIndex, endResultIndex)
  }, [currentPage, searchResults,rowsPerPage])

  /**
   * Calculate the loaded and next page index.
   * @return {number[]}
   */
  const getPagesIndexOptions = () => {
    const nextPageIndex = hasNext ? max(receivedPageIndexes) + 1 : null
    if (nextPageIndex) {
      return receivedPageIndexes.concat([nextPageIndex])
    } else {
      return receivedPageIndexes
    }
  }

  /**
   * Compose jump to page pagination control options.
   * @return {{label: string, value: string}[]}
   */
  const composeSelectOptions = () => {
    const pagesIndex = getPagesIndexOptions()
    return pagesIndex.map((option) => {
      return {
        label: `${option}`,
        value: `${option}`,
      }
    })
  }

  /**
   * Jump to a given page.
   * @type {HandleJumpToPage}
   * @param {string} pageIndex
   */
  const handleJumpToPage = (pageIndex) => {
    // Adapt page index to zero based index.
    const jumpPageIndex = Number(pageIndex) - 1
    handleChangePage(null, jumpPageIndex)
  }

  /**
   * Change number of items per page.
   * @type {HandleRowsPerPage}
   * @param {string} pageItemsNumber
   */
  const handleRowsPerPage = async (pageItemsNumber) => {
    setRowsPerPage(Number(pageItemsNumber))
  }

  const pagesIndexOptions = useMemo(() => composeSelectOptions(), [receivedPageIndexes, hasNext])
  const currentPageIndex = `${currentPage + 1}`

  // Re-run search when rows per page setting is changed.
  useEffect(() => {
    if (!isEmpty(currentFilters)) {
      setLoading(true)
      processSearch(
        {
          ...currentFilters,
          page: 1,
        },
        true
      ).then(() => {
        console.log('Rows pre page changed, updating search results.')
        setLoading(false)
      })
    }
  }, [rowsPerPage])

  return {
    handlers: {
      handleChangePage,
      handleJumpToPage,
      handleRowsPerPage,
      setLoading,
      setHasNext,
      setSearchResults,
      startNewSearch,
    },
    state: {
      currentFilters,
      currentPage,
      currentPageIndex,
      hasNext,
      loading,
      pageResults,
      pagesIndexOptions,
      resultsCount,
      rowsPerPage,
    },
  }
}

export default useSearchState
